import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey, phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import {
  type BrandAllQuerySchema,
  type BrandCreateSchema,
  type BrandQuerySchema,
  type BrandUpdateSchema,
} from './schemas';

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, BrandCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/brands/',
        payload,
      }),
  });

export function useGetBrands(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['BRANDS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<BrandAllQuerySchema>({
        endpoint: '/api/v1/brands',
        params: `${apiParamStr}&include=icon,logo`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['BRANDS'] });
};

export const useGetBrandsMutation: UseControlledMutation<BrandQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['BRANDS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<BrandAllQuerySchema>({
            endpoint: '/api/v1/brands',
            params: { search, include: 'icon', per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetBrand(id: string) {
  return useSuspenseQuery({
    queryKey: ['BRAND', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: BrandQuerySchema }>({
        endpoint: `/api/v1/brands/${id}`,
        params: {
          include: `icon,logo,logo_light,agreement_logo,invoice_logo,${phoneNumberIncludeKey()},${addressesIncludeKey()}`,
        },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: BrandQuerySchema }, ApiError, Partial<BrandUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/brands/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['BRAND_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/brands/${id}`,
        method: 'DELETE',
      }),
  });
